import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { UploadCloud, FileText, Loader2, X } from "lucide-react";
import { uploadActivity } from "../lib/api";
import { toast } from "sonner";

const GENERIC_UPLOAD_ERROR = "Upload failed. Please try again.";

/**
 * Turn a failed upload request into a short, user-visible reason.
 *
 * Handles the shapes FastAPI can return: string ``detail``, structured
 * ``{code, message}`` detail, plain axios network errors, and the case where
 * no response was received at all. Never leaks stack traces or codes.
 */
export const extractUploadErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message.trim();
    }
    if (typeof detail.reason === "string" && detail.reason.trim()) {
      return detail.reason.trim();
    }
  }
  const status = error?.response?.status;
  if (status === 413) return "File is too large to upload.";
  if (status === 415 || status === 400) {
    return "This file could not be read. Make sure it is a valid FIT, GPX, or TCX export.";
  }
  if (status === 409) {
    return "This activity was already uploaded.";
  }
  if (status && status >= 500) {
    return "The server could not process this file. Please try again shortly.";
  }
  if (error?.message && !/Network Error/i.test(error.message)) {
    return GENERIC_UPLOAD_ERROR;
  }
  if (/Network Error/i.test(error?.message || "")) {
    return "Cannot reach the server. Please check your connection.";
  }
  return GENERIC_UPLOAD_ERROR;
};

export const UploadDialog = ({ open, onOpenChange, onUploaded }) => {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList).filter((f) =>
      /\.(fit|gpx|tcx)$/i.test(f.name),
    );
    if (arr.length === 0) {
      toast.error("Only .FIT / .GPX / .TCX files are supported");
      return;
    }
    setFiles(arr);
    setBusy(true);
    let ok = 0;
    for (const f of arr) {
      try {
        const activity = await uploadActivity(f);
        onUploaded?.(activity);
        ok += 1;
      } catch (e) {
        toast.error(`${f.name}: ${extractUploadErrorMessage(e)}`);
      }
    }
    setBusy(false);
    setFiles([]);
    if (ok > 0) {
      toast.success(`${ok} activit${ok > 1 ? "ies" : "y"} uploaded`);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-dark-surface-1 border border-dark-border rounded-none max-w-xl p-0"
        data-testid="upload-dialog"
      >
        <DialogHeader className="p-6 border-b border-dark-border">
          <DialogTitle className="font-heading text-2xl tracking-wider uppercase text-dark-text-primary">
            Upload Activity
          </DialogTitle>
          <p className="text-xs tracking-[0.2em] uppercase text-dark-text-muted mt-1">
            Upload your activity file
          </p>
        </DialogHeader>

        <div className="p-6">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            className={`border border-dashed cursor-pointer p-10 text-center transition-colors ${
              dragOver ? "border-brand-primary bg-brand-primary/5" : "border-dark-border hover:border-brand-primary"
            }`}
            data-testid="upload-dropzone"
          >
            <UploadCloud className="w-10 h-10 mx-auto mb-4 text-brand-primary" strokeWidth={1.5} />
            <div className="font-mono text-sm tracking-widest text-dark-text-primary uppercase mb-2">
              Drop .FIT / .GPX / .TCX here
            </div>
            <div className="text-xs text-dark-text-muted">or click to browse files</div>
            <input
              ref={inputRef}
              type="file"
              accept=".fit,.gpx,.tcx"
              multiple
              className="hidden"
              onChange={(e) => e.target.files && handleFiles(e.target.files)}
              data-testid="upload-file-input"
            />
          </div>

          {busy && (
            <div className="mt-6 space-y-2">
              {files.map((f) => (
                <div
                  key={f.name}
                  className="flex items-center gap-3 border border-dark-border p-3"
                >
                  <FileText className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
                  <span className="flex-1 font-mono text-xs text-dark-text-primary truncate">{f.name}</span>
                  <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex items-start gap-2 text-[11px] text-dark-text-muted leading-relaxed">
            <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 rotate-45 text-dark-text-muted" />
            <span>
              Export activity files from Garmin Connect, Wahoo, Polar, or Strava
              (Settings → Download Original) and upload here.
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
